"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { Upload, Trash2, Loader2, CheckCircle, AlertCircle, Sparkles, Download, RefreshCw, Link2, Search, ExternalLink, Eye, EyeOff, Star, Flag, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useBranding } from "@/components/providers/branding-provider";

interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  total: number;
  errors: string[];
}

interface ProgressState {
  current: number;
  total: number;
  success: number;
  failed: number;
}

interface ResourceAuthor {
  id: string;
  username: string;
  name: string | null;
  avatar: string | null;
}

interface ResourceCategory {
  id: string;
  name: string;
  slug: string;
}

interface AdminResource {
  id: string;
  title: string;
  slug: string | null;
  type: string;
  isPrivate: boolean;
  isUnlisted: boolean;
  isFeatured: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  author: ResourceAuthor;
  category: ResourceCategory | null;
  _count: {
    votes: number;
    reports: number;
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ResourcesManagementProps {
  aiSearchEnabled: boolean;
  resourcesWithoutEmbeddings: number;
  totalPublicResources: number;
  resourcesWithoutSlugs: number;
  totalResources: number;
}

export function ResourcesManagement({ aiSearchEnabled, resourcesWithoutEmbeddings, totalPublicResources, resourcesWithoutSlugs, totalResources }: ResourcesManagementProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const branding = useBranding();

  // Disable import/delete community resources on main site (not clones)
  const canModifyCommunityResources = branding.useCloneBranding;
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingSlugs, setGeneratingSlugs] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [embeddingResult, setEmbeddingResult] = useState<{ success: number; failed: number } | null>(null);
  const [embeddingProgress, setEmbeddingProgress] = useState<ProgressState | null>(null);
  const [slugResult, setSlugResult] = useState<{ success: number; failed: number } | null>(null);
  const [slugProgress, setSlugProgress] = useState<ProgressState | null>(null);
  const [generatingRelated, setGeneratingRelated] = useState(false);
  const [relatedResult, setRelatedResult] = useState<{ success: number; failed: number } | null>(null);
  const [relatedProgress, setRelatedProgress] = useState<ProgressState | null>(null);

  // Resources list state
  const [resources, setResources] = useState<AdminResource[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingResources, setLoadingResources] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<AdminResource | null>(null);
  const [deletingResource, setDeletingResource] = useState(false);
  const [resourceFilter, setResourceFilter] = useState("all");

  const fetchResources = useCallback(async (page: number, search: string, filter: string) => {
    setLoadingResources(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(search && { search }),
        ...(filter !== "all" && { filter }),
      });
      const res = await fetch(`/api/admin/resources?${params}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to fetch resources");
      }

      setResources(data.resources);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to fetch resources");
    } finally {
      setLoadingResources(false);
    }
  }, []);

  useEffect(() => {
    fetchResources(currentPage, searchQuery, resourceFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resourceFilter, fetchResources]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchResources(1, searchQuery, resourceFilter);
  };

  const handleFilterChange = (value: string) => {
    setResourceFilter(value);
    setCurrentPage(1);
  };

  const handleDeleteResource = async () => {
    if (!resourceToDelete) return;

    setDeletingResource(true);
    try {
      const res = await fetch(`/api/admin/resources/${resourceToDelete.id}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to delete resource");
      }

      toast.success(t("admin.resourcesList.deleted"));
      setResourceToDelete(null);
      fetchResources(currentPage, searchQuery, resourceFilter);
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete resource");
    } finally {
      setDeletingResource(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setShowConfirm(false);
    setImportResult(null);

    try {
      const res = await fetch("/api/admin/import-resources", { method: "POST" });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Import failed");
      }

      setImportResult(data);

      if (data.imported > 0) {
        toast.success(t("admin.resources.importSuccess", { count: data.imported }));
        router.refresh();
      } else if (data.skipped === data.total) {
        toast.info(t("admin.resources.allSkipped"));
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setShowDeleteConfirm(false);
    setImportResult(null);

    try {
      const res = await fetch("/api/admin/import-resources", { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Delete failed");
      }

      toast.success(t("admin.resources.deleteSuccess", { count: data.deleted }));
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateEmbeddings = async (regenerate: boolean = false) => {
    setGenerating(true);
    setEmbeddingResult(null);
    setEmbeddingProgress(null);

    try {
      const url = regenerate ? "/api/admin/embeddings?regenerate=true" : "/api/admin/embeddings";
      const res = await fetch(url, { method: "POST" });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to generate embeddings");
      }

      // Read the stream
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split("admin.\n\n").filter(line => line.startsWith("data: "));

        for (const line of lines) {
          const jsonStr = line.replace("data: ", "");
          try {
            const data = JSON.parse(jsonStr);

            if (data.done) {
              setEmbeddingResult({ success: data.success, failed: data.failed });
              toast.success(t("admin.resources.embeddingsSuccess", { count: data.success }));
              router.refresh();
            } else {
              setEmbeddingProgress({
                current: data.current,
                total: data.total,
                success: data.success,
                failed: data.failed,
              });
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to generate embeddings");
    } finally {
      setGenerating(false);
      setEmbeddingProgress(null);
    }
  };

  const handleExport = () => {
    // Direct link to public resources.csv endpoint
    window.open("/resources.csv", "_blank");
    toast.success(t("admin.resources.exportSuccess"));
  };

  const handleGenerateRelatedResources = async () => {
    setGeneratingRelated(true);
    setRelatedResult(null);
    setRelatedProgress(null);

    try {
      const res = await fetch("/api/admin/related-resources", { method: "POST" });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to generate related resources");
      }

      // Read the stream
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split("admin.\n\n").filter(line => line.startsWith("data: "));

        for (const line of lines) {
          const jsonStr = line.replace("data: ", "");
          try {
            const data = JSON.parse(jsonStr);

            if (data.done) {
              setRelatedResult({ success: data.success, failed: data.failed });
              toast.success(t("admin.resources.relatedSuccess", { count: data.success }));
              router.refresh();
            } else {
              setRelatedProgress({
                current: data.current,
                total: data.total,
                success: data.success,
                failed: data.failed,
              });
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to generate related resources");
    } finally {
      setGeneratingRelated(false);
      setRelatedProgress(null);
    }
  };

  const handleGenerateSlugs = async (regenerate: boolean = false) => {
    setGeneratingSlugs(true);
    setSlugResult(null);
    setSlugProgress(null);

    try {
      const url = regenerate ? "/api/admin/slugs?regenerate=true" : "/api/admin/slugs";
      const res = await fetch(url, { method: "POST" });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to generate slugs");
      }

      // Read the stream
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split("admin.\n\n").filter(line => line.startsWith("data: "));

        for (const line of lines) {
          const jsonStr = line.replace("data: ", "");
          try {
            const data = JSON.parse(jsonStr);

            if (data.done) {
              setSlugResult({ success: data.success, failed: data.failed });
              toast.success(t("admin.resources.slugsSuccess", { count: data.success }));
              router.refresh();
            } else {
              setSlugProgress({
                current: data.current,
                total: data.total,
                success: data.success,
                failed: data.failed,
              });
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to generate slugs");
    } finally {
      setGeneratingSlugs(false);
      setSlugProgress(null);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">{t("admin.resources.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("admin.resources.description")}</p>
        </div>
      </div>

      <div className="rounded-md border p-3 sm:p-4 space-y-3">
        {/* Import Row */}
        {canModifyCommunityResources && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm text-muted-foreground flex-1">{t("admin.import.fileInfo")}</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowConfirm(true)}
                disabled={loading || deleting || generating}
                className="flex-1 sm:flex-none"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />{t("admin.resources.import")}</>}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading || deleting || generating}
                className="text-destructive hover:text-destructive"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        {/* Export Row */}
        <div className={`flex flex-col sm:flex-row sm:items-center gap-2 ${canModifyCommunityResources ? "pt-3 border-t" : ""}`}>
          <span className="text-sm text-muted-foreground flex-1">{t("admin.resources.exportInfo")}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            disabled={loading || deleting || generating}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />{t("admin.resources.export")}
          </Button>
        </div>

        {importResult && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {importResult.imported > 0 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
            <span>{t("admin.resources.importResult", { imported: importResult.imported, skipped: importResult.skipped })}</span>
          </div>
        )}

        {/* AI Embeddings Row */}
        {aiSearchEnabled && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-3 border-t">
              <span className="text-sm text-muted-foreground flex-1">
                {t("admin.aiSearch.title")} <span className="tabular-nums">({resourcesWithoutEmbeddings} {t("admin.resources.pending")})</span>
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleGenerateEmbeddings(false)}
                  disabled={loading || deleting || generating || resourcesWithoutEmbeddings === 0}
                  className="flex-1 sm:flex-none"
                >
                  {generating && !embeddingProgress ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : generating && embeddingProgress ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />{embeddingProgress.current}/{embeddingProgress.total}</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /><span className="hidden xs:inline">{t("admin.resources.generateEmbeddings")}</span><span className="xs:hidden">Generate</span></>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleGenerateEmbeddings(true)}
                  disabled={loading || deleting || generating || totalPublicResources === 0}
                  title={t("admin.resources.regenerateEmbeddings")}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Progress bar */}
            {embeddingProgress && (
              <div className="space-y-2">
                <Progress value={Math.round((embeddingProgress.current / embeddingProgress.total) * 100)} className="h-2" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{embeddingProgress.current} / {embeddingProgress.total}</span>
                  <span>{Math.round((embeddingProgress.current / embeddingProgress.total) * 100)}%</span>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-green-600">✓ {embeddingProgress.success}</span>
                  {embeddingProgress.failed > 0 && <span className="text-red-600">✗ {embeddingProgress.failed}</span>}
                </div>
              </div>
            )}

            {embeddingResult && !embeddingProgress && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {embeddingResult.failed === 0 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
                <span>{t("admin.resources.embeddingsResult", { success: embeddingResult.success, failed: embeddingResult.failed })}</span>
              </div>
            )}
          </>
        )}

        {/* URL Slugs Row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-3 border-t">
          <span className="text-sm text-muted-foreground flex-1">
            {t("admin.resources.slugsTitle")} <span className="tabular-nums">({resourcesWithoutSlugs} {t("admin.resources.pending")})</span>
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleGenerateSlugs(false)}
              disabled={loading || deleting || generating || generatingSlugs || resourcesWithoutSlugs === 0}
              className="flex-1 sm:flex-none"
            >
              {generatingSlugs && !slugProgress ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : generatingSlugs && slugProgress ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />{slugProgress.current}/{slugProgress.total}</>
              ) : (
                <><Link2 className="h-4 w-4 mr-2" />{t("admin.resources.generateSlugs")}</>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleGenerateSlugs(true)}
              disabled={loading || deleting || generating || generatingSlugs || totalResources === 0}
              title={t("admin.resources.regenerateSlugs")}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Slug Progress bar */}
        {slugProgress && (
          <div className="space-y-2">
            <Progress value={Math.round((slugProgress.current / slugProgress.total) * 100)} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{slugProgress.current} / {slugProgress.total}</span>
              <span>{Math.round((slugProgress.current / slugProgress.total) * 100)}%</span>
            </div>
            <div className="flex gap-4 text-xs">
              <span className="text-green-600">✓ {slugProgress.success}</span>
              {slugProgress.failed > 0 && <span className="text-red-600">✗ {slugProgress.failed}</span>}
            </div>
          </div>
        )}

        {slugResult && !slugProgress && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {slugResult.failed === 0 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
            <span>{t("admin.resources.slugsResult", { success: slugResult.success, failed: slugResult.failed })}</span>
          </div>
        )}

        {/* Related Resources Row */}
        {aiSearchEnabled && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-3 border-t">
              <span className="text-sm text-muted-foreground flex-1">
                {t("admin.resources.relatedTitle")}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateRelatedResources}
                disabled={loading || deleting || generating || generatingSlugs || generatingRelated}
                className="w-full sm:w-auto"
              >
                {generatingRelated && !relatedProgress ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : generatingRelated && relatedProgress ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />{relatedProgress.current}/{relatedProgress.total}</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />{t("admin.resources.regenerateRelated")}</>
                )}
              </Button>
            </div>

            {/* Related Progress bar */}
            {relatedProgress && (
              <div className="space-y-2">
                <Progress value={Math.round((relatedProgress.current / relatedProgress.total) * 100)} className="h-2" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{relatedProgress.current} / {relatedProgress.total}</span>
                  <span>{Math.round((relatedProgress.current / relatedProgress.total) * 100)}%</span>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-green-600">✓ {relatedProgress.success}</span>
                  {relatedProgress.failed > 0 && <span className="text-red-600">✗ {relatedProgress.failed}</span>}
                </div>
              </div>
            )}

            {relatedResult && !relatedProgress && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {relatedResult.failed === 0 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
                <span>{t("admin.resources.relatedResult", { success: relatedResult.success, failed: relatedResult.failed })}</span>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.resources.importConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.resources.importConfirmDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("admin.resources.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleImport}>{t("admin.resources.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.resources.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.resources.deleteConfirmDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("admin.resources.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90 text-white">
              {t("admin.resources.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resources List Section */}
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold">{t("admin.resourcesList.title")}</h3>
            <p className="text-sm text-muted-foreground">{t("admin.resourcesList.description")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Select value={resourceFilter} onValueChange={(value) => value && handleFilterChange(value)}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("admin.resourcesList.filters.all")}</SelectItem>
                <SelectItem value="public">{t("admin.resourcesList.filters.public")}</SelectItem>
                <SelectItem value="private">{t("admin.resourcesList.filters.private")}</SelectItem>
                <SelectItem value="unlisted">{t("admin.resourcesList.filters.unlisted")}</SelectItem>
                <SelectItem value="featured">{t("admin.resourcesList.filters.featured")}</SelectItem>
                <SelectItem value="reported">{t("admin.resourcesList.filters.reported")}</SelectItem>
                <SelectItem value="deleted">{t("admin.resourcesList.filters.deleted")}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("common.search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-9 w-full sm:w-[200px]"
                />
              </div>
              <Button size="icon" variant="outline" onClick={handleSearch} disabled={loadingResources}>
                {loadingResources ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile-friendly Resources Cards */}
        <div className="space-y-3">
          {loadingResources && resources.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {t("admin.resourcesList.noResources")}
            </div>
          ) : (
            resources.map((prompt) => (
              <div
                key={prompt.id}
                className="rounded-lg border bg-card p-4 space-y-3"
              >
                {/* Header Row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium truncate">{prompt.title}</h4>
                      {prompt.isFeatured && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                      )}
                      {prompt.isPrivate && (
                        <Badge variant="secondary" className="text-xs">
                          <EyeOff className="h-3 w-3 mr-1" />
                          {t("admin.resourcesList.private")}
                        </Badge>
                      )}
                      {prompt.isUnlisted && (
                        <Badge variant="outline" className="text-xs">
                          <Eye className="h-3 w-3 mr-1" />
                          {t("admin.resourcesList.unlisted")}
                        </Badge>
                      )}
                      {prompt._count.reports > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          <Flag className="h-3 w-3 mr-1" />
                          {prompt._count.reports}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {prompt.type} • {prompt.viewCount} {t("admin.resourcesList.views")} • {prompt._count.votes} {t("admin.resourcesList.votes")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {prompt.id && (
                      <Link href={`/registry/${prompt.id}`} target="_blank" prefetch={false}>
                        <Button size="icon" variant="ghost" className="h-8 w-8">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setResourceToDelete(prompt)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Author & Category Row */}
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={prompt.author.avatar || undefined} />
                      <AvatarFallback className="text-xs">
                        {prompt.author.name?.[0] || prompt.author.username[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-muted-foreground truncate">
                      @{prompt.author.username}
                    </span>
                  </div>
                  {prompt.category && (
                    <Badge variant="secondary" className="text-xs truncate max-w-[150px]">
                      {prompt.category.name}
                    </Badge>
                  )}
                </div>

                {/* Date Row */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("admin.resourcesList.created")}: {new Date(prompt.createdAt).toLocaleDateString()}</span>
                  <span className="font-mono text-[10px] opacity-50">{prompt.id.slice(0, 8)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {t("admin.resourcesList.showing", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                disabled={currentPage === 1 || loadingResources}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm tabular-nums px-2">
                {currentPage} / {pagination.totalPages}
              </span>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                disabled={currentPage === pagination.totalPages || loadingResources}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Single Resource Confirmation Dialog */}
      <AlertDialog open={!!resourceToDelete} onOpenChange={(open) => !open && setResourceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.resourcesList.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.resourcesList.deleteConfirmDescription", { title: resourceToDelete?.title || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={deletingResource}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteResource}
              disabled={deletingResource}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              {deletingResource ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
